import { useState, type JSX } from "react";

import { ExclamationCircleFilled } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import FormCardContainer from "@impero-com/common/components/formCardContainer";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { scrollToFirstErroredElement } from "@impero-com/common/utils/form/erroredElements";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Button,
    FormSubtitle,
    FormTitle,
    renderMarkdown,
} from "@impero-com/ui";
import { Popover, notification } from "antd";
import { isEmpty } from "lodash-es";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
    RiskDirectoryResourceDto as RiskDirectoryDto,
    RiskDirectoryResource,
    UpdatedRiskDirectoryStructure,
} from "api/generated/model";
import { StickyFooter } from "components/stickyFooter/footer";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import { WebRoute } from "../../../../routes";

import { StructureForm } from "./form";
import {
    buildApiPayloadFromFormValues,
    buildFormValuesFromApi,
} from "./formData";
import { useRiskDirectoryStructureMutation } from "./queries/useRiskDirectoryStructureMutation";
import { useRiskDirectoryStructureQuery } from "./queries/useRiskDirectoryStructureQuery";
import { FormSchema, formSchema } from "./schema";
import * as theme from "./theme.css";

export const StructureEditorView = () => {
    const { id } = useParams();
    const riskDirectoryId = Number(id);
    if (Number.isNaN(riskDirectoryId)) {
        throw new Error("Risk directory ID should be a number");
    }

    const { data, error, status } =
        useRiskDirectoryStructureQuery(riskDirectoryId);
    const { mutateAsync, isPending } =
        useRiskDirectoryStructureMutation(riskDirectoryId);

    switch (status) {
        case "pending":
            return <p>Loading Risk Directory Structure...</p>;
        case "error":
            return <LoggedInHTTPError code={error.response?.status} />;
        case "success":
            const riskDirectoryLevelProcessFields =
                data.content.resource.structure.nodeTypeToDirectoryLevelFields[
                    "Process"
                ];
            if (
                !riskDirectoryLevelProcessFields ||
                isEmpty(riskDirectoryLevelProcessFields)
            ) {
                return (
                    <StructureEditor
                        riskDirectoryDto={data}
                        updateRiskDirectory={mutateAsync}
                        mutationPending={isPending}
                    />
                );
            } else {
                return (
                    <FormCardContainer fullWidth={true}>
                        <FormTitle title={"Editor unavailable"} />
                        <FormSubtitle
                            subtitle={
                                "The risk structure editor is unavailable for this risk directory" +
                                ' because it contains risk directory level fields for "Process" nodes.' +
                                " This is not supported... yet."
                            }
                        />
                    </FormCardContainer>
                );
            }
        default:
            return assertNever(status);
    }
};

interface StructureEditorProps {
    riskDirectoryDto: RiskDirectoryDto;
    updateRiskDirectory: (
        updated: UpdatedRiskDirectoryStructure
    ) => Promise<RiskDirectoryResource>;
    mutationPending: boolean;
}

function StructureEditor(props: StructureEditorProps): JSX.Element {
    const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

    const values = buildFormValuesFromApi(
        props.riskDirectoryDto.content.resource
    );
    const { control, handleSubmit, reset, formState, getValues } =
        useForm<FormSchema>({
            resolver: zodResolver(
                formSchema(
                    props.riskDirectoryDto.content.resource.structure
                        .blessedFieldIds
                )
            ),
            values,
            mode: "onBlur",
        });

    const handleSubmitClick = handleSubmit(
        () => {
            setConfirmSubmitOpen(true);
            // While the submit confirmation is open, form is assumed to be in valid state
        },
        (error) => {
            console.error(error, getValues());
            scrollToFirstErroredElement();
            notification.error({
                message: "There are errors in your Risk Directory Structure",
            });
        }
    );

    const onSubmit = async () => {
        const data: FormSchema = getValues();
        const payload: UpdatedRiskDirectoryStructure =
            buildApiPayloadFromFormValues(data);
        const updatedRiskDirectory = await props.updateRiskDirectory(payload);
        notification.success({
            message:
                "Changes to the risk directory structure have successfully been submitted",
        });
        reset(buildFormValuesFromApi(updatedRiskDirectory));
        setConfirmSubmitOpen(false);
    };

    const handlePopupVisibleChange = (visible: boolean) => {
        // Ignore true - controlled
        // Handle false - on click outside popover
        if (visible === false) {
            setConfirmSubmitOpen(false);
        }
    };

    return (
        <OrgApplicationBar
            title={{
                title: props.riskDirectoryDto.content.resource.title,
                breadCrumbs: [
                    {
                        url: WebRoute.RISKS,
                        label: t("RiskModule_MenuTitle"),
                    },
                ],
            }}
        >
            <div>
                <StructureForm
                    control={control}
                    blessedFieldIds={
                        props.riskDirectoryDto.content.resource.structure
                            .blessedFieldIds
                    }
                />
            </div>
            <StickyFooter>
                <Popover
                    open={confirmSubmitOpen}
                    onOpenChange={handlePopupVisibleChange}
                    trigger="click"
                    content={
                        <div className={theme.confirmRoot}>
                            <div className={theme.confirmWarnText}>
                                <ExclamationCircleFilled
                                    className={theme.confirmWarnIcon}
                                />
                                {renderMarkdown(
                                    "WARNING: You are about to modify the risk details or assessment fields " +
                                        "of this risk map. This action may **permanently DELETE customer risk " +
                                        "assessment data** or the existing assessment schema.\n\n" +
                                        "Please use the Risk Customizer only for setting up a new risk map. " +
                                        "**If you need to modify an existing risk map already in use by customers, " +
                                        "consult the technical team first.**\n\n" +
                                        "Before proceeding, you must back up the data by exporting the affected " +
                                        "risk map."
                                )}
                            </div>
                            <div className={theme.confirmButtons}>
                                <Button
                                    variant="default"
                                    onClick={() => setConfirmSubmitOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => void onSubmit()}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    }
                >
                    <Button
                        onClick={() => void handleSubmitClick()}
                        size="large"
                        variant="primary"
                        loading={props.mutationPending}
                        disabled={!formState.isDirty}
                    >
                        Submit
                    </Button>
                </Popover>
            </StickyFooter>
        </OrgApplicationBar>
    );
}
